// ============================================================================
// OUTPUT PROFILE SERVICE (ADR-0001, slice GRC-2)
// ============================================================================
// crs.Api.Controllers.DocumentModule.OutputProfilesController -> /api/v1/output-profiles
// crs.Api.Controllers.CheckInModule.GrcCardController         -> /api/v1/grc

import { del, get, post, put } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, ID } from '../types';
import type { OutputDocumentType, OutputProfile, SaveOutputProfileRequest } from '../types/outputProfile.types';

const BASE = '/output-profiles';

/** The signed-card scan on file (GRC-4). */
export interface SignedCardInfo {
  hasSignedCard: boolean;
  uploadedAt: string | null;
  uploadedByName: string | null;
}

const unwrap = <T,>(res: { data?: ApiResponse<T> }, fallback: string): T => {
  if (res.data?.data === undefined || res.data?.data === null) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

/** A data: URL or bare base64 (the application logo kept at login) as an uploadable file. */
export const base64ToFile = (value: string, name: string): File => {
  const match = value.match(/^data:([^;]+);base64,(.*)$/);
  const type = match?.[1] ?? 'image/png';
  const bytes = atob(match?.[2] ?? value);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
  const ext = type.split('/')[1] ?? 'png';
  return new File([buffer], `${name}.${ext}`, { type });
};

class OutputProfileService {
  async get(propertyId: ID, documentType: OutputDocumentType): Promise<OutputProfile> {
    return unwrap(await get<ApiResponse<OutputProfile>>(buildEndpoint(`${BASE}/${propertyId}/${documentType}`)), 'The print layout did not load.');
  }

  async save(propertyId: ID, documentType: OutputDocumentType, request: SaveOutputProfileRequest): Promise<OutputProfile> {
    return unwrap(await put<ApiResponse<OutputProfile>>(buildEndpoint(`${BASE}/${propertyId}/${documentType}`), request), 'The print layout was not saved.');
  }

  async uploadLogo(propertyId: ID, documentType: OutputDocumentType, file: File): Promise<OutputProfile> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await post<ApiResponse<OutputProfile>>(buildEndpoint(`${BASE}/${propertyId}/${documentType}/logo`), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res, 'The logo was not saved.');
  }

  async removeLogo(propertyId: ID, documentType: OutputDocumentType): Promise<OutputProfile> {
    return unwrap(await del<ApiResponse<OutputProfile>>(buildEndpoint(`${BASE}/${propertyId}/${documentType}/logo`)), 'The logo was not removed.');
  }

  /** The logo image, for the screen's preview. */
  async logo(propertyId: ID, documentType: OutputDocumentType): Promise<Blob> {
    const res = await get<Blob>(buildEndpoint(`${BASE}/${propertyId}/${documentType}/logo`), { responseType: 'blob' });
    return res.data;
  }

  async copyFrom(propertyId: ID, documentType: OutputDocumentType, fromPropertyId: ID): Promise<OutputProfile> {
    return unwrap(await post<ApiResponse<OutputProfile>>(
      buildEndpointWithQuery(`${BASE}/${propertyId}/${documentType}/copy`, { fromPropertyId }), {}), 'The layout was not copied.');
  }

  /** The document with sample data, printed with the saved layout. */
  async preview(propertyId: ID, documentType: OutputDocumentType): Promise<Blob> {
    const res = await get<Blob>(buildEndpoint(`${BASE}/${propertyId}/${documentType}/preview`), { responseType: 'blob' });
    return res.data;
  }

  /** Files the scan of the signed card (PDF / JPEG / PNG / WebP), replacing any earlier one (GRC-4). */
  async uploadSignedCard(bookingId: ID, file: File): Promise<SignedCardInfo> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await post<ApiResponse<SignedCardInfo>>(buildEndpoint(`/grc/${bookingId}/signed-card`), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(res, 'The signed card was not filed.');
  }

  /** The scan of the signed card. */
  async signedCard(bookingId: ID): Promise<Blob> {
    const res = await get<Blob>(buildEndpoint(`/grc/${bookingId}/signed-card`), { responseType: 'blob' });
    return res.data;
  }

  /** The booking's registration card (A4 PDF), pre-filled for the guest to complete and sign. */
  async grcPdf(bookingId: ID): Promise<Blob> {
    const res = await get<Blob>(buildEndpoint(`/grc/${bookingId}/pdf`), { responseType: 'blob' });
    return res.data;
  }
}

export const outputProfileService = new OutputProfileService();
export default outputProfileService;
